import {useContext, useState} from 'react';
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
    USER_ARTICLES_KEY,
    USER_CURRENT_KEY
} from "../api/public-voissa/users";
import useAbility from "./useAbility";
import {ModalContext} from "../ui/components2/ModalManager";
import USER_ROLES from "../const/userRoles";

import {
    createCommentLike,
    createLike,
    createSubCommentLike,
    deleteCommentLike,
    deleteLike, deleteSubCommentLike
} from "../api/public-voissa/likes";
import {ARTICLE_KEY} from "../api/public-voissa/articles";
import {MEDIA_KEY, MEDIAS_KEY} from "../api/public-voissa/medias";


const useLikeMedia = ({
    mediaId = null,
    isLiked = false,
    handleError = () =>{},
    handleSuccess = () =>{}
}) =>{

    const queryClient = useQueryClient();

    const {
        mutate : handleMediaLike
    } = useMutation(() => isLiked ? deleteLike("medias", mediaId): createLike("medias", mediaId),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([MEDIA_KEY, mediaId]);
            await queryClient.invalidateQueries([MEDIAS_KEY]);
            handleSuccess();
        },
        onError: () =>{
            handleError();
        }
    });

    return {handleMediaLike};
}

const useLikeStatus = ({
                          statusId = null,
                          isLiked = false,
                          handleError = () =>{},
                          handleSuccess = () =>{}
                      }) =>{

    const {
        mutate : handleStatusLike
    } = useMutation(() => isLiked ? deleteLike("status", statusId): createLike("status", statusId),{
        onSuccess: async () =>{
            handleSuccess();
        },
        onError: () =>{
            handleError();
        }
    });

    return {handleStatusLike};
}

const useLikeArticle = ({
      articleId = null,
      isLiked = false,
      handleError = () =>{},
      handleSuccess = () =>{}
    }) =>{

    const queryClient = useQueryClient();
    const {
        mutate : handleArticleLike,
    } = useMutation(() => isLiked ? deleteLike("articles", articleId) : createLike("articles", articleId),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([ARTICLE_KEY, articleId]);
            await queryClient.invalidateQueries([USER_ARTICLES_KEY]);
            handleSuccess();
        },
        onError: () =>{
            handleError();
        }
    });

    return {handleArticleLike};
}

const useLikeComment = ({
        parentKind = "",
        parentId = null,
        commentId = null,
        subCommentId = null,
        isLiked = false,
        handleError = () =>{},
        handleSuccess = () =>{}
    }) =>{

    const createLike = () =>{subCommentId ? createSubCommentLike(parentKind, parentId, commentId, subCommentId) : createCommentLike(parentKind, parentId, commentId)};
    const removeLike = () =>{subCommentId ? deleteSubCommentLike(parentKind, parentId, commentId, subCommentId) : deleteCommentLike(parentKind, parentId, commentId)};

    const {
        mutate : handleCommentLike,
    } = useMutation(() => isLiked ? removeLike() : createLike(),{
        onSuccess: () =>{
            handleSuccess();
        },
        onError: () =>{
            handleError();
        }
    });

    return {handleCommentLike};
}

const useLike = ({
      parentKind  = "",
      parentId,
      commentId,
      subCommentId,
      isLike = false,
      onSuccess = () =>{},
      onError = () =>{}
    }) =>{

    const queryClient = useQueryClient();
    const {can} = useAbility();
    parentKind = parentKind?.toLowerCase();

    const currentUser = queryClient.getQueryData([USER_CURRENT_KEY]);
    const canLike =  can({ action:'create', subject:'Like' });
    const { openModal } = useContext(ModalContext);
    const [isLiked, setIsLiked] = useState(isLike);

    function handleError (){
        setIsLiked(!isLiked);
        onError();
    }
    async function handleSuccess (){
        setIsLiked(!isLiked);
        onSuccess();
    }

    const {handleMediaLike} = useLikeMedia({mediaId : parentId, isLiked, handleError, handleSuccess});
    const {handleStatusLike} = useLikeStatus({statusId : parentId, isLiked, handleError, handleSuccess});
    const {handleArticleLike} = useLikeArticle({articleId : parentId, isLiked, handleError, handleSuccess });
    const {handleCommentLike} = useLikeComment({parentKind, parentId, commentId, subCommentId, isLiked, handleError, handleSuccess});

    const handleLikeMapping = {
        medias: handleMediaLike,
        status: handleStatusLike,
        articles: handleArticleLike,
        comments: handleCommentLike
    }

    function handleLike() {
        if(canLike){
            handleLikeMapping[parentKind]();
        }
        else {
            switch (currentUser?.role) {
                case USER_ROLES.EMAIL_UNCONFIRMED:
                    openModal('EmailValidation', {toggle : true, isModal : true});
                    break;
                default :
                    openModal('EncourageSignup');
            }
        }
    }

    return {isLiked, handleLike };
};

export default useLike;